import {useSearchParams} from 'react-router-dom';
import React, { useState, useEffect, useRef } from 'react';
import { transactionsAPI, clientsAPI } from '../services/api';
import { ArrowUpCircle, ArrowDownCircle, Search } from 'lucide-react';
import Pagination from '../components/Pagination';
import toast from 'react-hot-toast';

const PER_PAGE = 20;

function BonusModal({ type, onClose, onDone }) {
  const [form, setForm] = useState({ phone: '', amount: '', comment: '' });
  const [loading, setLoading] = useState(false);
  const isAccrue = type === 'accrue';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = { ...form, amount: parseFloat(form.amount) };
      if (isAccrue) {
        await transactionsAPI.accrue(data);
        toast.success(`Начислено ${data.amount} баллов`);
      } else {
        await transactionsAPI.deduct(data);
        toast.success(`Списано ${data.amount} баллов`);
      }
      onDone();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-title">{isAccrue ? 'Начислить бонусы' : 'Списать бонусы'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Телефон клиента *</label>
            <input className="form-input" required placeholder="+79001234567"
              value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Сумма баллов *</label>
            <input className="form-input" type="number" required min="0.01" step="0.01"
              placeholder="100" value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Комментарий *</label>
            <input className="form-input" required placeholder="Причина"
              value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Отмена</button>
            <button type="submit" className={`btn ${isAccrue ? 'btn-success' : 'btn-danger'}`} disabled={loading}>
              {loading ? '...' : isAccrue ? 'Начислить' : 'Списать'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function TransactionsPage() {
  const [urlFilters,setUrlFilters]=useSearchParams();
  const detailFilters=Object.fromEntries(['kind','date_from','date_to'].filter(k=>urlFilters.get(k)).map(k=>[k,urlFilters.get(k)]));
  const detailKey=JSON.stringify(detailFilters);
  const [modal, setModal] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const requestId = useRef(0);
  const retryLoad = useRef(null);
  const [page, setPage] = useState(1);
  const [filterPhone, setFilterPhone] = useState('');
  const [isFiltered, setIsFiltered] = useState(false);

  const loadAll = (p = page) => {
    const id = ++requestId.current;
    retryLoad.current = () => loadAll(p);
    setLoading(true);
    setLoadError('');
    const skip = (p - 1) * PER_PAGE;
    return transactionsAPI.all(skip, PER_PAGE,detailFilters).then((res) => {
      if (id !== requestId.current) return;
      setTransactions(res.data.items);
      setTotal(res.data.total);
      setIsFiltered(false);
    }).catch(() => {
      if (id === requestId.current) setLoadError('Не удалось загрузить транзакции. Повторите попытку.');
    }).finally(() => {
      if (id === requestId.current) setLoading(false);
    });
  };

  const loadByPhone = async () => {
    if (!filterPhone) { loadAll(1); setPage(1); return; }
    const id = ++requestId.current;
    retryLoad.current = loadByPhone;
    setLoading(true);
    setLoadError('');
    try {
      const phone = filterPhone.trim();
      const clientRes = await clientsAPI.list(0, 200, phone);
      if (id !== requestId.current) return;
      const client = clientRes.data.items.find((c) => c.phone === phone);
      if (!client) { setLoadError('Клиент не найден. Проверьте телефон и повторите поиск.'); return; }
      const res = await transactionsAPI.history(client.id, 0, 200);
      if (id !== requestId.current) return;
      setTransactions(res.data.items);
      setTotal(res.data.total);
      setIsFiltered(true);
      setPage(1);
    } catch {
      if (id === requestId.current) setLoadError('Не удалось загрузить транзакции клиента. Повторите попытку.');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (!isFiltered) loadAll(page);
  }, [page,detailKey]);
  useEffect(() => () => { ++requestId.current; }, []);

  const totalPages = isFiltered ? 1 : Math.ceil((total || 0) / PER_PAGE);

  const typeLabel = (type) => {
    const t = (type || '').toLowerCase();
    const map = {
      accrual: { text: 'Начисление', cls: 'badge-success' },
      deduction: { text: 'Списание', cls: 'badge-danger' },
      hold: { text: 'Заморозка', cls: 'badge-warning' },
      hold_confirm: { text: 'Подтверждение', cls: 'badge-info' },
      hold_cancel: { text: 'Возврат', cls: 'badge-success' },
      refund: { text: '\u0412\u043e\u0437\u0432\u0440\u0430\u0442 \u0431\u043e\u043d\u0443\u0441\u043e\u0432', cls: 'badge-success' },
      expiration: { text: 'Сгорание', cls: 'badge-danger' },
    };
    const m = map[t] || { text: type, cls: 'badge-info' };
    return <span className={`badge ${m.cls}`}>{m.text}</span>;
  };

  return (
    <div className="animate-in">
      <div className="page-header">
        <h1 className="page-title">Транзакции</h1>
        <p className="page-subtitle">{total === null ? (loadError ? 'Количество недоступно' : 'Загружаем транзакции…') : `Всего операций: ${total}`}</p>
      </div>

      <div className="page-actions">
        <button className="btn btn-success" onClick={() => setModal('accrue')}>
          <ArrowUpCircle size={16} /> Начислить
        </button>
        <button className="btn btn-danger" onClick={() => setModal('deduct')}>
          <ArrowDownCircle size={16} /> Списать
        </button>
        <div style={{ flex: 1 }} />
        {!Object.keys(detailFilters).length&&<><input className="form-input" style={{ maxWidth: 240 }}
          placeholder="Фильтр по телефону"
          value={filterPhone}
          onChange={(e) => setFilterPhone(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && loadByPhone()} />
        <button className="btn btn-ghost" onClick={loadByPhone}>
          <Search size={16} />
        </button></>}
        {isFiltered && (
          <button className="btn btn-ghost" onClick={() => { setFilterPhone(''); setPage(1); loadAll(1); }}>
            Сброс
          </button>
        )}
      </div>

      {Object.keys(detailFilters).length>0&&<p style={{fontSize:12}}>Выборка с дашборда: {{accrual:'Начисления',hold_confirm:'Оплата бонусами',expiration:'Сгорания',deduction:'Списания и корректировки',refund:'Возвраты'}[detailFilters.kind]||'Все операции'} · {detailFilters.date_from} — {detailFilters.date_to} <button className="btn btn-ghost btn-sm" onClick={()=>{setUrlFilters({});setPage(1)}}>Сбросить</button></p>}
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Дата</th>
              <th>Тип</th>
              <th>Сумма</th>
              <th>Комментарий</th>
            </tr>
          </thead>
          <tbody aria-busy={loading}>
            {loading && <tr><td colSpan={4} role="status" style={{ padding: 16, color: 'var(--text-muted)' }}>{transactions.length ? 'Обновляем список…' : 'Загружаем транзакции…'}</td></tr>}
            {loadError && <tr><td colSpan={4} role="alert" style={{ padding: 16 }}>{loadError} <button className="btn btn-sm" onClick={() => retryLoad.current?.()}>Повторить</button></td></tr>}
            {transactions.map((t) => (
              <tr key={t.id}>
                <td>{new Date(t.created_at).toLocaleString('ru')}</td>
                <td>{typeLabel(t.type)}</td>
                <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                  {['deduction','hold','hold_confirm','expiration'].includes(t.type.toLowerCase()) ? '−' : '+'}
                  {t.amount.toLocaleString('ru')}
                </td>
                <td>{t.comment || '—'}</td>
              </tr>
            ))}
            {!loading && !loadError && total !== null && transactions.length === 0 && (
              <tr><td colSpan={4} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                Транзакций пока нет
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {modal && <BonusModal type={modal} onClose={() => setModal(null)} onDone={() => loadAll(page)} />}
    </div>
  );
}